import type { GoalInsight } from '../../../hooks/useAdvancedInsights';

interface Props { goals: GoalInsight[]; }

const paceStyle: Record<GoalInsight['pace'], { bg: string; text: string; icon: string }> = {
  on_track: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'ri-checkbox-circle-line' },
  at_risk: { bg: 'bg-amber-50', text: 'text-amber-600', icon: 'ri-error-warning-line' },
  behind: { bg: 'bg-red-50', text: 'text-red-500', icon: 'ri-alarm-warning-line' },
  overdue: { bg: 'bg-red-50', text: 'text-red-500', icon: 'ri-time-line' },
  completed: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'ri-trophy-line' },
};

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const InsightsGoalsSection = ({ goals }: Props) => {
  if (!goals.length) return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-xl">
          <i className="ri-focus-3-line text-emerald-500 text-[16px]" />
        </div>
        <h2 className="text-[15px] font-700 text-gray-900">Metas</h2>
      </div>
      <div className="bg-gray-50 rounded-2xl border border-gray-100 px-5 py-8 text-center">
        <i className="ri-focus-3-line text-[28px] text-gray-300 mb-2 block" />
        <p className="text-[13px] text-gray-400">Nenhuma meta ativa. Crie metas para acompanhar seu progresso aqui.</p>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-xl">
          <i className="ri-focus-3-line text-emerald-500 text-[16px]" />
        </div>
        <div>
          <h2 className="text-[15px] font-700 text-gray-900">Análise de Metas</h2>
          <p className="text-[12px] text-gray-400">Status e projeções das suas metas ativas</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {goals.map((g) => {
          const s = paceStyle[g.pace];
          return (
            <div key={g.id} className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-7 h-7 flex items-center justify-center ${s.bg} rounded-xl`}>
                  <i className={`${s.icon} ${s.text} text-[14px]`} />
                </div>
                <p className="text-[13px] font-700 text-gray-800 truncate flex-1">{g.name}</p>
              </div>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-gray-400">{g.progress}% concluído</span>
                  {g.remaining > 0 && <span className="text-[11px] text-gray-500">Faltam {fmt(g.remaining)}</span>}
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${g.progress}%` }} />
                </div>
              </div>
              <p className={`text-[12px] font-400 ${s.text}`}>{g.message}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
